import { matchPresentationPhase } from "./matchPresentation";
import { countValue } from "./upcomingMatch";
import { rankBullseyeRows } from "../Leaderboard/bullseyePostedRanking";
import { bullseyeTieContext } from "../Leaderboard/bullseyeTieBreaks";

export const isInProgressDetail = (match, now) => Boolean(match && matchPresentationPhase(match, now) === "in_progress");
const number = value => value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value)) ? Number(value) : null;
const rowKey = row => String(row.entryId || row.competitorId || row.participantId || row.sk || "");
const confirmed = row => String(row.reviewStatus || "").toLowerCase() === "confirmed";
const eligible = row => row.isRankEligible !== false && !["dq", "dnf", "withdrawn"].includes(String(row.outcomeStatus || "active").toLowerCase());
const outcomeLabel = row => ({ dq: "DQ", dnf: "DNF", withdrawn: "Withdrawn" })[String(row.outcomeStatus || "").toLowerCase()] || "Not ranked";
const commonRow = (row, index) => ({
  id: String(row.sk || row.entryId || row.competitorId || row.participantId || index),
  name: row.competitorName || row.displayName || "Competitor",
  division: String(row.divisionId || row.division || "").trim(),
  classification: String(row.classification || "").trim(),
  raw: row,
});

export function arcLiveSections(match, matchRows = [], stageRows = [], stageItems = []) {
  const expectedStages = (Array.isArray(match.stages) && match.stages.length) || (Array.isArray(match.stageIds) && match.stageIds.length) || countValue(match.expectedStageCount) || 0;
  const progress = new Map();
  stageRows.filter(confirmed).forEach(row => {
    const key = rowKey(row); const stageId = row.stageId || row.matchStageId;
    if (!key || !stageId) return;
    if (!progress.has(key)) progress.set(key, new Set());
    progress.get(key).add(stageId);
  });
  function convert(row, index, stage = false) {
    const score = number(stage ? row.stageScore : row.totalScore);
    const complete = confirmed(row) && (stage || row.canonicalAggregate === true || (expectedStages > 0 && (progress.get(rowKey(row))?.size || 0) >= expectedStages));
    const rankEligible = complete && eligible(row) && score !== null && score >= 0;
    const scopeId = stage ? row.matchStageId : match.matchId;
    return { ...commonRow(row, index), score, xCount: 0, rankEligible, participation: "Overall",
      scoreLabel: score !== null ? score.toFixed(2) : "—",
      progressLabel: !eligible(row) ? outcomeLabel(row) : stage ? confirmed(row) ? "Confirmed" : "Awaiting review"
        : row.canonicalAggregate ? "Complete" : expectedStages ? `${progress.get(rowKey(row))?.size || 0}/${expectedStages} stages confirmed` : "Stage count unavailable",
      state: !eligible(row) ? "excluded" : complete ? "complete" : "partial",
      detailPath: scopeId && row.sk && row.s3Key && !row.resultId ? `/matches/results/${stage ? "stage" : "match"}/${encodeURIComponent(scopeId)}/${encodeURIComponent(row.sk)}` : null,
    };
  }
  return [{ key: "overall", label: "Overall standings", rows: matchRows.map((row, index) => convert(row, index)) },
    ...stageItems.map((stage, index) => ({ key: stage.stageId, label: stage.name && stage.name !== stage.stageId ? stage.name : `Stage ${index + 1}`,
      rows: stageRows.filter(row => row.stageId === stage.stageId || row.matchStageId === stage.stageId).map((row, index) => convert(row, index, true)),
    }))];
}

export function bullseyeLiveSections(rows = [], postedSections = []) {
  const aggregate = postedSections.find(section => section.kind === "match");
  const contexts = new Map((aggregate?.entrantRows || aggregate?.rows || []).map(row => [row.sourceRowKey, row.tieContext]));
  const convert = (row, index) => {
    const score = number(row.totalScore);
    const xCount = number(row.xCount) ?? 0;
    const shots = countValue(row.shotsFired);
    const planned = countValue(row.plannedShots);
    const hasScore = score > 0 || xCount > 0 || shots > 0;
    const categories = Array.isArray(row.categories) ? row.categories : String(row.category || row.categories || "").split(",");
    const rimfireOnly = row.twentyTwoOnly || categories.some(value => ["22only", "22caliberonly", "22calibreonly", "twentytwoonlyaggregate"].includes(String(value).toLowerCase().replace(/[^a-z0-9]/g, "")));
    return { ...commonRow(row, index), score, xCount, shotsFired: shots,
      tieContext: row.tieContext || contexts.get(commonRow(row, index).id) || bullseyeTieContext(row),
      rankEligible: eligible(row) && hasScore && score !== null,
      participation: rimfireOnly ? ".22 Only" : "Standard",
      scoreLabel: hasScore && score !== null ? `${score}–${xCount}X` : "—",
      progressLabel: !eligible(row) ? outcomeLabel(row) : shots !== null && planned > 0 ? `${shots}/${planned} shots` : shots !== null && hasScore ? `${shots} shots` : hasScore ? "Score received" : "Awaiting scores",
      state: !eligible(row) ? "excluded" : hasScore ? "complete" : "partial",
    };
  };
  return [{ key: "overall", label: "Overall standings", rows: rows.map(convert) },
    ...postedSections.filter(section => section.kind !== "match").map(section => ({ key: section.key, label: section.displayName || section.title, rows: section.rows.map(convert) }))];
}

export function rankLiveRows(rows, family, division = "all") {
  const filtered = division === "all" ? rows : rows.filter(row => row.division === division);
  const groups = family === "bullseye" ? ["Standard", ".22 Only"] : ["Overall"];
  return groups.flatMap(group => {
    if (family === "bullseye") {
      const grouped = filtered.filter(row => row.participation === group);
      const ranked = rankBullseyeRows(grouped.filter(row => row.rankEligible).map(row => ({
        ...row, totalScore: row.score, competitorName: row.name,
      }))).map(row => ({ ...row, tied: row.isTied }));
      return [...ranked, ...grouped.filter(row => !row.rankEligible).sort((a,b) => a.name.localeCompare(b.name))
        .map(row => ({ ...row, rank: null, tied: false }))];
    }
    const sorted = filtered.filter(row => row.participation === group).sort((a, b) => Number(b.rankEligible) - Number(a.rankEligible)
      || (a.rankEligible && b.rankEligible ? family === "arc" ? a.score - b.score : b.score - a.score || b.xCount - a.xCount : 0)
      || a.name.localeCompare(b.name));
    const counts = new Map();
    const scoreKey = row => `${row.score}:${family === "bullseye" ? row.xCount : ""}`;
    sorted.filter(row => row.rankEligible).forEach(row => counts.set(scoreKey(row), (counts.get(scoreKey(row)) || 0) + 1));
    let previous; let place = 0;
    return sorted.map((row, index) => {
      if (!row.rankEligible) return { ...row, rank: null, tied: false };
      const key = scoreKey(row);
      if (key !== previous) place = index + 1;
      previous = key;
      return { ...row, rank: place, tied: counts.get(key) > 1 };
    });
  });
}

export function registrationSquadLabel(registration, squads = []) {
  if (!registration) return "";
  const squad = squads.find(item => registration.squadId && (item.squadId || item.id) === registration.squadId)
    || squads.find(item => registration.squadNumber != null && String(item.squadNumber ?? item.number) === String(registration.squadNumber));
  const squadNumber = squad?.squadNumber ?? squad?.number ?? registration.squadNumber;
  return squad?.label || (squadNumber != null ? `Squad ${squadNumber}` : "Squad not assigned");
}
